import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Trophy } from "lucide-react";
import { currencyLabel, visibleCurrencies } from "../lib/currencyDisplay";

export default function LeaderboardAdmin() {
  const [sortBy, setSortBy] = useState("free_currency");
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      const { data } = await api.get(`/leaderboard?sort_by=${sortBy}&limit=500`);
      setData(data);
    };
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [sortBy]);

  const currencies = useMemo(() => visibleCurrencies(data?.currencies || []), [data]);
  const activeCurrency = currencies.find((c) => c.id === sortBy) || currencies[0];

  return (
    <div className="space-y-6" data-testid="admin-leaderboard-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Channel points</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2"><Trophy className="w-5 h-5" style={{ color: activeCurrency?.color }} /> Leaderboard</h1>
        </div>
        <div className="flex flex-wrap gap-1 surface-2 p-1 rounded">
          {currencies.map((c) => (
            <button
              key={c.id}
              onClick={() => setSortBy(c.id)}
              data-testid={`admin-sort-${c.id}`}
              className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded ${sortBy === c.id ? "text-white" : "text-[hsl(var(--muted-foreground))] hover:text-white"}`}
              style={{ background: sortBy === c.id ? c.color : "transparent" }}
            >
              {currencyLabel(c)}
            </button>
          ))}
          <button
            onClick={() => setSortBy("watchtime")}
            data-testid="admin-sort-watchtime"
            className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded ${sortBy === "watchtime" ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))] hover:text-white"}`}
          >
            Active Watchtime
          </button>
        </div>
      </div>

      <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] px-4 py-3 text-sm text-[hsl(var(--muted-foreground))]">
        Active watchtime counts viewers who have chatted recently while the stream is live. It does not count silent lurk time yet.
      </div>

      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="py-3 px-4 w-12">#</th>
              <th>Viewer</th>
              {currencies.map((c) => <th key={c.id} className="text-right min-w-28" style={{ color: c.color }}>{currencyLabel(c)}</th>)}
              <th className="text-right">Active Watchtime</th>
            </tr>
          </thead>
          <tbody>
            {data?.viewers?.length ? data.viewers.map((v, i) => (
              <tr key={v.twitch_user_id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-2))]">
                <td className="py-2 px-4 font-mono text-[hsl(var(--muted-foreground))]">{i + 1}</td>
                <td>
                  <div className="flex items-center gap-2">
                    {v.profile_image_url && <img src={v.profile_image_url} alt="" className="w-6 h-6 rounded-full" />}
                    <span>{v.display_name}</span>
                  </div>
                </td>
                {currencies.map((c) => <td key={c.id} className="text-right font-mono" style={{ color: c.color }}>{v.balances?.[c.id] || 0}</td>)}
                <td className="text-right font-mono text-[hsl(var(--muted-foreground))]">{v.watchtime_minutes}m</td>
              </tr>
            )) : (
              <tr><td colSpan={currencies.length + 3} className="py-10 text-center text-[hsl(var(--muted-foreground))]">No viewers yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
